import React, { useState } from "react";
import { Avatar } from "../shared/Avatar";
import { fullName, formatMemberNumber } from "../../lib/format";

// ============================================================
// Iter 86 — TransferModal
// Bulk-transfer confirmation modal. The destination status
// options depend on the current source group tab.
// ============================================================
export function TransferModal({ sourceGroup, selectedMembers, busy, onCancel, onConfirm }) {
  // Iter 86 — destination options depend on the current group tab.
  const OPTIONS_BY_SOURCE = {
    members:  [{ status: "Visitor",    label: "Visitors" }, { status: "Guest", label: "Guests" }],
    visitors: [{ status: "New Member", label: "Members"  }, { status: "Guest", label: "Guests" }],
    guests:   [{ status: "New Member", label: "Members"  }, { status: "Visitor", label: "Visitors" }],
    all:      [
      { status: "New Member", label: "Members" },
      { status: "Visitor",    label: "Visitors" },
      { status: "Guest",      label: "Guests" },
    ],
  };
  const options = OPTIONS_BY_SOURCE[sourceGroup] || OPTIONS_BY_SOURCE.all;
  const [destStatus, setDestStatus] = useState(options[0]?.status || "New Member");
  const destOpt = options.find(o => o.status === destStatus) || options[0];
  const count = selectedMembers.length;
  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && !busy && onCancel()} data-testid="members-transfer-modal">
      <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title" data-testid="members-transfer-modal-title">Transfer {count} {count === 1 ? "person" : "people"}</div>
          <button className="modal-close" onClick={onCancel} disabled={busy}>×</button>
        </div>
        <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div
            data-testid="members-transfer-list"
            style={{
              maxHeight: 260, overflowY: "auto",
              border: "1px solid var(--kc-border)",
              borderRadius: 10,
              padding: 6,
              background: "color-mix(in srgb, var(--kc-text) 3%, transparent)",
            }}
          >
            {selectedMembers.map(m => (
              <div key={m.id} data-testid={`members-transfer-row-${m.id}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: 8 }}>
                <Avatar name={fullName(m)} photo={m.photo} status={m.memberStatus} style={{ width: 32, height: 32, fontSize: 11 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "var(--kc-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fullName(m)}</div>
                  <div style={{ fontSize: 11, color: "var(--kc-sub)" }}>
                    {m.memberStatus}
                    {m.memberNumber ? <> · <span style={{ fontFamily: "monospace" }}>{formatMemberNumber(m.memberNumber)}</span></> : null}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div>
            <div style={{ fontSize: 11, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700, marginBottom: 8 }}>
              Destination group
            </div>
            <div role="radiogroup" style={{ display: "flex", flexWrap: "wrap", gap: 8 }} data-testid="members-transfer-destination">
              {options.map(o => (
                <button
                  key={o.status}
                  type="button"
                  role="radio"
                  aria-checked={destStatus === o.status}
                  data-testid={`members-transfer-dest-${o.label.toLowerCase()}`}
                  onClick={() => setDestStatus(o.status)}
                  style={{
                    padding: "10px 18px",
                    borderRadius: 999,
                    border: `1px solid ${destStatus === o.status ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 25%, transparent)"}`,
                    background: destStatus === o.status ? "var(--kc-accent)" : "transparent",
                    color: destStatus === o.status ? "#111" : "var(--kc-text)",
                    fontWeight: 700, fontSize: 12,
                    letterSpacing: "0.06em", textTransform: "uppercase",
                    cursor: busy ? "not-allowed" : "pointer",
                  }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <div
            data-testid="members-transfer-summary"
            style={{
              padding: 12,
              borderRadius: 10,
              background: "color-mix(in srgb, var(--kc-accent) 8%, transparent)",
              border: "1px solid color-mix(in srgb, var(--kc-accent) 22%, transparent)",
              fontSize: 13,
              color: "var(--kc-text)",
              lineHeight: 1.5,
            }}
          >
            You are about to move {count} {count === 1 ? "person" : "people"} to <b>{destOpt?.label}</b>. This will change their status to <b>{destStatus}</b>. This cannot be undone without a manual status change.
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={onCancel}
              disabled={busy}
              data-testid="members-transfer-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm(destStatus)}
              disabled={busy}
              data-testid="members-transfer-confirm"
              style={{
                background: "var(--kc-gold)", color: "#111",
                border: "none", borderRadius: 8,
                padding: "10px 20px",
                fontFamily: "'Cinzel',serif", fontWeight: 700,
                letterSpacing: "0.06em", textTransform: "uppercase",
                fontSize: 12, cursor: busy ? "not-allowed" : "pointer",
              }}
            >
              {busy ? "Transferring…" : "Confirm Transfer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
